import { useState, useEffect } from 'react';
import { Users, ScanSearch, TrendingUp, BarChart3, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { KpiCard } from '@/components/dashboard/KpiCard';
import { adminAPI } from '@/lib/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend } from 'recharts';

const CHART_COLORS = ['#1AD1E6', '#10B981', '#F59E0B', '#DC2626', '#A9B4C7', '#00C2A8'];

export function UsageDashboardTab() {
  const [summary, setSummary] = useState(null);
  const [scansData, setScansData] = useState([]);
  const [roleData, setRoleData] = useState([]);
  const [domainData, setDomainData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [sumRes, scansRes, roleRes, domainRes] = await Promise.all([
          adminAPI.getUsageSummary(),
          adminAPI.getScansPerDay(30),
          adminAPI.getUsersByRole(),
          adminAPI.getTopDomains(),
        ]);
        setSummary(sumRes.data);
        setScansData(scansRes.data.data || []);
        setRoleData(roleRes.data.data || []);
        setDomainData(domainRes.data.data || []);
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-usage-dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard title="Total Users" value={summary?.total_users || 0} subtitle={`${summary?.active_users || 0} active`} icon={Users} />
        <KpiCard title="Active Subscriptions" value={summary?.active_subscriptions || 0} subtitle="Paid plans" icon={TrendingUp} />
        <KpiCard title="Total Scans" value={summary?.total_scans || 0} icon={ScanSearch} />
        <KpiCard title="Scans This Month" value={summary?.scans_this_month || 0} icon={BarChart3} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Scans per Day */}
        <Card className="border-border/60">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Scans Per Day (30 days)</CardTitle></CardHeader>
          <CardContent>
            {scansData.length === 0 ? (
              <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">No scan data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={scansData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.16)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#A9B4C7' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#A9B4C7' }} />
                  <Tooltip contentStyle={{ background: '#0A1020', border: '1px solid #1B263B', borderRadius: 8, fontSize: 12, color: '#f0f4f8' }} />
                  <Line type="monotone" dataKey="count" stroke="#1AD1E6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Users by Role */}
        <Card className="border-border/60">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Users by Role</CardTitle></CardHeader>
          <CardContent>
            {roleData.length === 0 ? (
              <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">No user data</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={roleData} dataKey="count" nameKey="role" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} label={({ role, count }) => `${role} (${count})`}>
                    {roleData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#0A1020', border: '1px solid #1B263B', borderRadius: 8, fontSize: 12, color: '#f0f4f8' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Top Domains */}
        <Card className="border-border/60 xl:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Top Scanned Domains</CardTitle></CardHeader>
          <CardContent>
            {domainData.length === 0 ? (
              <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">No scan data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={domainData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.16)" />
                  <XAxis dataKey="domain" tick={{ fontSize: 10, fill: '#A9B4C7' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#A9B4C7' }} />
                  <Tooltip contentStyle={{ background: '#0A1020', border: '1px solid #1B263B', borderRadius: 8, fontSize: 12, color: '#f0f4f8' }} />
                  <Bar dataKey="count" fill="#1AD1E6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
